import { readPath } from "~/helpers/offline/idempotencyKey";

export const QUEUE_DETAIL_ID_SUFFIX_LENGTH = 6;
export const QUEUE_DETAIL_MAX_LENGTH = 60;

export type DetailToken =
	| { kind: "amount"; path: string }
	| { kind: "id"; path: string }
	| { kind: "route"; fromPaths: string[]; toPaths: string[] };

const DETAIL_SEPARATOR = " · ";

const ID_TOKEN_PREFIX = "#";

const readInputValue = (variables: unknown, fieldPath: string): unknown => {
	if (!variables || typeof variables !== "object") return undefined;
	const { input } = variables as { input?: unknown };
	return readPath(input ?? variables, fieldPath);
};

const truncateText = (value: string, maxLength: number): string =>
	value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;

const shortId = (value: string): string => `${ID_TOKEN_PREFIX}${value.slice(-QUEUE_DETAIL_ID_SUFFIX_LENGTH)}`;

const firstNonEmptyString = (variables: unknown, paths: string[]): string | undefined => {
	for (const path of paths) {
		const value = readInputValue(variables, path);
		if (typeof value === "string" && value.length > 0) return value;
	}
	return undefined;
};

const renderDetailToken = (variables: unknown, token: DetailToken): string | undefined => {
	if (token.kind === "id") {
		const value = readInputValue(variables, token.path);
		return typeof value === "string" && value.length > 0 ? shortId(value) : undefined;
	}
	if (token.kind === "route") {
		const from = firstNonEmptyString(variables, token.fromPaths);
		const to = firstNonEmptyString(variables, token.toPaths);
		return from && to ? `${shortId(from)}→${shortId(to)}` : undefined;
	}
	const value = readInputValue(variables, token.path);
	if (typeof value === "number" && Number.isFinite(value)) return `×${value}`;
	return Array.isArray(value) && value.length > 0 ? `×${value.length}` : undefined;
};

const buildQueueDetail = (variables: unknown, tokens: DetailToken[] | undefined): string | undefined => {
	if (!tokens || tokens.length === 0) return undefined;
	const rendered = tokens
		.map((token) => renderDetailToken(variables, token))
		.filter((value): value is string => value !== undefined);
	if (rendered.length === 0) return undefined;
	return truncateText(rendered.join(DETAIL_SEPARATOR), QUEUE_DETAIL_MAX_LENGTH);
};

export const stripIdTokensFromQueueDetail = (detail: string | null): string | null => {
	const shown = (detail ?? "").split(DETAIL_SEPARATOR).filter((token) => token && !token.startsWith(ID_TOKEN_PREFIX));
	return shown.length > 0 ? shown.join(DETAIL_SEPARATOR) : null;
};

export default buildQueueDetail;
